import type { MapboxOverlayProps } from '@deck.gl/mapbox';
import { MapboxOverlay } from '@deck.gl/mapbox';
import { BitmapLayer } from 'deck.gl';
import * as GeoTIFF from 'geotiff';
import proj4 from 'proj4';
import React, { useEffect, useMemo, useState } from 'react';
import { useControl } from 'react-map-gl/mapbox';

import { hexToRgb } from '@/utils/helpers';


export enum LandCoverCategory {
  Artificial,
  Agricultural,
  Natural,
  Water,
}

export const LandCoverColors: Record<LandCoverCategory, string> = {
  [LandCoverCategory.Artificial]: '#FE222A',
  [LandCoverCategory.Agricultural]: '#D4A017',
  [LandCoverCategory.Natural]: '#388E3C',
  [LandCoverCategory.Water]: '#0077C2',
};

export const LandCoverColorsFaded: Record<LandCoverCategory, string> = {
  [LandCoverCategory.Artificial]: '#FF8A88',
  [LandCoverCategory.Agricultural]: '#E6C871',
  [LandCoverCategory.Natural]: '#4FAE63',
  [LandCoverCategory.Water]: '#66B3E0',
};

const LandCoverClassification: Record<number, LandCoverCategory> = {
  10: LandCoverCategory.Natural,  // Tree Cover
  20: LandCoverCategory.Natural,  // Shrubland
  30: LandCoverCategory.Natural,  // Grassland
  40: LandCoverCategory.Agricultural, // Cropland
  50: LandCoverCategory.Artificial,   // Built-up
  60: LandCoverCategory.Natural,  // Bare / Sparse Vegetation
  70: LandCoverCategory.Natural,  // Snow and Ice
  80: LandCoverCategory.Water,    // Permanent Water Bodies
  90: LandCoverCategory.Natural,  // Herbaceous Wetland
  95: LandCoverCategory.Natural,  // Mangroves
  100: LandCoverCategory.Natural, // Moss and Lichen
};

interface RawRasterData {
  data: Uint8Array;
  width: number;
  height: number;
  bbox: [number, number, number, number];
}

interface RasterData {
  width: number;
  height: number;
  imageBitmap: ImageBitmap;
  bbox: [number, number, number, number];
}

function DeckGLOverlay(props: MapboxOverlayProps): null {
  const overlay = useControl<MapboxOverlay & { interleaved?: boolean }>(() => new MapboxOverlay(props));
  overlay.setProps(props);
  return null;
}

interface Props {
  tiff: string | null;
  hoveredLandCover: LandCoverCategory | null;
  setHoveredLandCover: (category: LandCoverCategory | null) => void;
  opacity?: number;
}

export default function LandCoverMapLayer({ tiff, hoveredLandCover, setHoveredLandCover, opacity = 0.4 }: Props): React.JSX.Element | null {
  const [rawRasterData, setRawRasterData] = useState<RawRasterData | null>(null);
  const [landCoverRasterData, setLandCoverRasterData] = useState<RasterData | null>(null);

  // Parse TIFF once per site — no dependency on hoveredLandCover
  useEffect(() => {
    if (!tiff) { setRawRasterData(null); return; }
    const load = async (): Promise<void> => {
      const cleanBase64 = tiff.split(',')[1] ?? tiff;
      const binary = atob(cleanBase64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      const geotiff = await GeoTIFF.fromArrayBuffer(bytes.buffer);
      const image = await geotiff.getImage();
      const width = image.getWidth();
      const height = image.getHeight();
      const raster = (await image.readRasters({ interleave: true })) as Uint8Array;
      const bboxMeters = image.getBoundingBox() as [number, number, number, number];
      const [minX, minY, maxX, maxY] = bboxMeters;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const crs = image.getGeoKeys()?.ProjectedCSTypeGeoKey as string;
      const [lonMin, latMin] = proj4(`EPSG:${crs}`, 'EPSG:4326', [minX, minY]);
      const [lonMax, latMax] = proj4(`EPSG:${crs}`, 'EPSG:4326', [maxX, maxY]);
      setRawRasterData({ data: raster, width, height, bbox: [lonMin, latMin, lonMax, latMax] });
    };
    void load();
  }, [tiff]);

  // Re-render colored bitmap when raw data or hovered category changes
  useEffect(() => {
    if (!rawRasterData) { setLandCoverRasterData(null); return; }
    const render = async (): Promise<void> => {
      const { data, width, height, bbox } = rawRasterData;
      const rgba = new Uint8ClampedArray(width * height * 4);
      for (let i = 0; i < data.length; i++) {
        const value = data[i];
        const landCoverCategory = LandCoverClassification[value ?? -1] ?? null;
        if (landCoverCategory == null) {
          rgba.set([0, 0, 0, 0], i * 4);
          continue;
        }
        const color =
          hoveredLandCover != null && hoveredLandCover != landCoverCategory
            ? LandCoverColorsFaded[landCoverCategory]
            : LandCoverColors[landCoverCategory];
        const rgb = hexToRgb(color);
        const opacity = hoveredLandCover != null && hoveredLandCover != landCoverCategory ? 170 : 255;
        rgba.set([rgb.r, rgb.g, rgb.b, opacity], i * 4);
      }
      const imageData = new ImageData(rgba, width, height);
      const imageBitmap = await createImageBitmap(imageData);
      setLandCoverRasterData({ width, height, imageBitmap, bbox });
    };
    void render();
  }, [rawRasterData, hoveredLandCover]);

  const landCoverLayer = useMemo(() => {
    if (!landCoverRasterData) return null;
    return new BitmapLayer({
      id: 'landCover',
      image: landCoverRasterData.imageBitmap,
      bounds: landCoverRasterData.bbox,
      opacity,
      beforeId: 'settlement-major-label',
      pickable: true,
      onHover: (info) => {
        const { bitmap } = info as { bitmap?: { pixel: [number, number] } };
        if (!info.picked || !bitmap || !rawRasterData) {
          setHoveredLandCover(null);
          return;
        }
        const [x, y] = bitmap.pixel;
        const idx = y * rawRasterData.width + x;
        const value = rawRasterData.data[idx];
        const category = LandCoverClassification[value ?? -1] ?? null;
        setHoveredLandCover(category);
      },
    });
  }, [landCoverRasterData, rawRasterData, setHoveredLandCover]);

  return (
    <DeckGLOverlay
      layers={[landCoverLayer]}
      interleaved
      getCursor={({ isHovering }) => isHovering ? 'pointer' : 'grab'}
    />
  );
}
